import Cookies from 'js-cookie'
import { defineStore } from 'pinia'
import { z } from 'zod'
import { useLogin, type UseInfiniteLoginType } from '@/composables/use-login'
import type { OtpLoginToken } from '@/models/api/otp-login-token'
import { userProfileSchema, type UserProfile } from '@/models/api/user-profile'
import { CookiesKey } from '@library/enums/cookies-key'
import { SessionStorageKey } from '@library/enums/session-storage-key'
import { useStorage } from '~library/composables'
import { Identity, LocalStorage } from '~library/enums'
import { isType } from '~library/utils/functions'

const optDataSchema = z.object({
  account: z.string(),
  email: z.string(),
  otpRemainingTime: z.number(),
  sseCode: z.string(),
  identity: z.nativeEnum(Identity),
  userProfile: userProfileSchema
})

type OtpData = z.infer<typeof optDataSchema>

/** OTP 驗證 Store，管理 OTP 驗證碼的發送、倒數、過期與跨分頁同步 */
export const useOtpStore = defineStore('otp', () => {
  const { setDataAfterLogin } = useLogin()
  const { setStorageData, removeStorageData, getStorageData } = useStorage()

  // 在 store 初始化時立即取得導航類型，後續查詢時 entries 可能已被清空
  const navigationType = ref<PerformanceNavigationTiming['type'] | null>(
    (
      window.performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming
    ).type
  )

  const _otpData = ref<OtpData | null>(null)
  const _otpLoginToken = ref<OtpLoginToken | null>(null)
  const _otpIsExpired = ref<boolean>(false)
  const _otpIsReload = ref<boolean>(false)

  /** OTP 驗證碼剩餘有效秒數 */
  const otpRemainingTime = computed(
    (): number | undefined => _otpData.value?.otpRemainingTime
  )

  /** OTP 驗證資料 */
  const otpData = computed((): OtpData | null => _otpData.value)

  /** OTP 登入 Token */
  const otpLoginToken = computed(
    (): OtpLoginToken | null => _otpLoginToken.value
  )

  /** OTP 驗證碼是否已過期 */
  const otpIsExpired = computed((): boolean => _otpIsExpired.value)

  /** 頁面是否為重新整理進入 */
  const otpIsReload = computed((): boolean => _otpIsReload.value)

  /** 設定 OTP 驗證資料並同步至 Storage */
  const setOtpData = (
    userProfile: UserProfile,
    loginData: UseInfiniteLoginType
  ) => {
    _otpData.value = {
      account: userProfile.memberCardNumber,
      email: userProfile.email,
      otpRemainingTime: userProfile.otpRemainingTime,
      sseCode: userProfile.sseCode,
      identity: loginData.loginData.identity,
      userProfile: userProfile
    }

    setOtpIsReload(false)
    setOtpLastStep(false)
    setOtpStorage()
  }

  /** 清除 otp data */
  const clearOtpData = () => {
    _otpData.value = null
    setOtpIsReload(false)
    clearOtpStorage()
  }

  /** 更新 OTP 倒數時間並同步至 Storage */
  const setOtpRemainingTime = (data: OtpData) => {
    _otpData.value = data
    setOtpIsReload(false)
    setOtpStorage()
  }

  /** 標記 OTP 驗證碼已過期並清除資料 */
  const otpDataExpired = () => {
    _otpIsExpired.value = true
    setOtpIsExpired(true)
    clearOtpData()
  }

  /** 重置 OTP 過期狀態為未過期 */
  const otpDataUnExpired = () => {
    _otpIsExpired.value = false
    setOtpIsExpired(false)
  }

  /**
   * 同時存入 Cookie、localStorage、sessionStorage：
   * Cookie 用於跨分頁同步，sessionStorage 用於同分頁 reload 還原，
   * localStorage 記錄最後更新時間以利過期判斷
   */
  const setOtpStorage = () => {
    Cookies.set(CookiesKey.OTP_DATA, JSON.stringify(otpData.value))
    setStorageData(LocalStorage.OTP_DATA, Date.now())
    sessionStorage.setItem(
      SessionStorageKey.OTP_DATA,
      JSON.stringify(otpData.value)
    )
  }

  /** otp 刪除Storage */
  const clearOtpStorage = () => {
    Cookies.remove(CookiesKey.OTP_DATA)
    removeStorageData(LocalStorage.OTP_DATA)
    sessionStorage.removeItem(SessionStorageKey.OTP_DATA)
  }

  /**
   * 取得 OTP 暫存資料：
   * reload 時從 sessionStorage 還原（Cookie 可能被其他分頁覆寫），
   * 一般導航時從 Cookie 取得，確保跨分頁狀態同步
   */
  const getSessionOtpData = () => {
    setOtpLastStep(false)
    if (navigationType.value === 'reload') {
      const otpDataFromSessionStorage = JSON.parse(
        sessionStorage.getItem(SessionStorageKey.OTP_DATA) ?? 'null'
      )

      if (isType(otpDataFromSessionStorage, optDataSchema)) {
        _otpData.value = otpDataFromSessionStorage
      }

      setOtpStorage()
      setOtpIsReload(true)
      navigationType.value = null
    } else {
      const otpDataFromCookie = Cookies.get(CookiesKey.OTP_DATA)
      if (
        otpDataFromCookie &&
        isType(JSON.parse(otpDataFromCookie), optDataSchema)
      ) {
        _otpData.value = JSON.parse(otpDataFromCookie)
        sessionStorage.setItem(SessionStorageKey.OTP_DATA, otpDataFromCookie)
      }
    }
  }

  /** 設定頁面是否為重新整理進入 */
  const setOtpIsReload = (value: boolean) => {
    _otpIsReload.value = value
  }

  /** OTP 驗證通過後，儲存登入 Token 並寫入 DeviceToken Cookie */
  const setOtpLoginToken = (data: OtpLoginToken) => {
    _otpLoginToken.value = data
    Cookies.set(CookiesKey.DEVICE_TOKEN, data.deviceToken)
  }

  /** OTP 驗證成功後執行登入流程 */
  const otpVerifySuccess = () => {
    if (otpData.value) {
      setDataAfterLogin({
        account: otpData.value.account,
        identity: otpData.value.identity,
        userProfile: { ...otpData.value.userProfile, ...otpLoginToken.value },
        setLang: true
      })
    }
    defaultOtpData()
  }

  /** 從 Cookie 同步其他分頁的 OTP 資料 */
  const asyncOptData = () => {
    const otpDataFromCookie = Cookies.get(CookiesKey.OTP_DATA)
    if (
      otpDataFromCookie &&
      isType(JSON.parse(otpDataFromCookie), optDataSchema)
    ) {
      _otpData.value = JSON.parse(otpDataFromCookie)
      sessionStorage.setItem(SessionStorageKey.OTP_DATA, otpDataFromCookie)
      setOtpLastStep(false)
    }
  }

  /** 初始化所有 OTP 資料為預設狀態 */
  const defaultOtpData = () => {
    clearOtpData()
    otpDataUnExpired()
    _otpLoginToken.value = null
    setOtpIsReload(false)
    navigationType.value = null
  }

  /** 初始化所有 OTP 資料並顯示已過期提示 */
  const defaultOtpDataWithExpired = () => {
    clearOtpData()
    otpDataExpired()
    setOtpIsReload(false)
    navigationType.value = null
  }

  /** 設定 OTP 過期狀態至 localStorage */
  const setOtpIsExpired = (value: boolean) => {
    setStorageData(LocalStorage.OTP_IS_EXPIRED, value)
  }

  /** 設定 OTP 上一步狀態至 localStorage */
  const setOtpLastStep = (value: boolean) => {
    setStorageData(LocalStorage.OTP_LAST_STEP, value)
  }

  /** 取得 OTP 上一步狀態 */
  const getOtpLastStep = () => {
    return getStorageData(LocalStorage.OTP_LAST_STEP)
  }

  return {
    /** OTP 驗證資料 */
    otpData,
    /** OTP 登入 Token */
    otpLoginToken,
    /** OTP 驗證碼剩餘有效秒數 */
    otpRemainingTime,
    /** 頁面是否為重新整理進入 */
    otpIsReload,
    /** OTP 驗證碼是否已過期 */
    otpIsExpired,
    /** 從 Storage 取得 OTP 暫存資料 */
    getSessionOtpData,
    /** 標記 OTP 驗證碼已過期並清除資料 */
    otpDataExpired,
    /** 重置 OTP 過期狀態為未過期 */
    otpDataUnExpired,
    /** 從 Cookie 同步其他分頁的 OTP 資料 */
    asyncOptData,
    /** 更新 OTP 倒數時間並同步至 Storage */
    setOtpRemainingTime,
    /** 設定頁面是否為重新整理進入 */
    setOtpIsReload,
    /** OTP 驗證成功後執行登入流程 */
    otpVerifySuccess,
    /** 設定 OTP 驗證資料並同步至 Storage */
    setOtpData,
    /** OTP 驗證通過後，儲存登入 Token 並寫入 DeviceToken Cookie */
    setOtpLoginToken,
    /** 初始化所有 OTP 資料為預設狀態 */
    defaultOtpData,
    /** 初始化所有 OTP 資料並顯示已過期提示 */
    defaultOtpDataWithExpired,
    /** 設定 OTP 上一步狀態至 localStorage */
    setOtpLastStep,
    /** 取得 OTP 上一步狀態 */
    getOtpLastStep,
    /** 設定 OTP 過期狀態至 localStorage */
    setOtpIsExpired
  }
})
